import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Flame, Globe2, Clock } from "lucide-react";
import { api, mediaUrl } from "@/lib/api";
import { ALL_HUBS, COUNTRIES, hubByKey, applyHubGlow, fmtNum } from "@/lib/hubs";
import { useT } from "@/lib/i18n";
import { TrackRow } from "@/components/TrackItems";
import { ArtistBadge } from "@/components/ArtistBadge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const W = 1000, H = 480;
const proj = (lat, lng) => [((lng + 180) / 360) * W, ((90 - lat) / 180) * H];

const WorldMap = ({ countries, selected, onSelect }) => {
  const max = Math.max(1, ...countries.map((c) => c.streams));
  const dots = [];
  for (let y = 12; y < H; y += 16) for (let x = 8; x < W; x += 16) dots.push([x, y]);
  return (
    <svg data-testid="charts-world-map" viewBox={`0 0 ${W} ${H}`} className="w-full h-auto">
      {dots.map(([x, y], i) => <circle key={i} cx={x} cy={y} r={1.1} fill="rgba(255,255,255,0.07)" />)}
      {countries.map((c) => {
        const [x, y] = proj(c.lat, c.lng);
        const r = 6 + Math.sqrt(c.streams / max) * 34;
        const on = selected === c.code;
        return (
          <g key={c.code} data-testid={`map-country-${c.code}`} onClick={() => onSelect(on ? "GLOBAL" : c.code)} className="cursor-pointer">
            <circle cx={x} cy={y} r={r} fill="var(--hub)" opacity={on ? 0.55 : 0.22} className="transition-opacity" />
            <circle cx={x} cy={y} r={3.5} fill={on ? "#fff" : "var(--hub)"} />
            <text x={x} y={y - r - 6} textAnchor="middle" className="font-mono" fontSize="13" fill={on ? "#fff" : "rgba(255,255,255,0.6)"}>{c.code}</text>
          </g>
        );
      })}
    </svg>
  );
};

const ArtistLine = ({ a, i, extra }) => (
  <Link to={`/artist/${a.slug}`} data-testid={`chart-artist-${a.slug}`} className="flex items-center gap-3 p-2.5 rounded-xl hover:bg-white/5">
    <span className="w-5 text-right font-mono text-xs text-zinc-500">{i + 1}</span>
    <img src={mediaUrl(a.avatar)} alt="" className="w-10 h-10 rounded-full object-cover bg-zinc-800" />
    <div className="min-w-0 flex-1"><div className="text-sm font-semibold flex items-center gap-1 truncate">{a.name} <ArtistBadge badge={a.badge} /></div><div className="text-xs text-zinc-500">{COUNTRIES[a.country]}</div></div>
    <span className="font-mono text-xs text-zinc-300">{extra}</span>
  </Link>
);

export default function Charts() {
  const t = useT();
  const [hub, setHub] = useState("all");
  const [country, setCountry] = useState("GLOBAL");
  const [d, setD] = useState(null);
  useEffect(() => {
    api.get("/charts", { params: { hub, country } }).then((r) => setD(r.data));
    applyHubGlow(hub === "all" ? null : hubByKey(hub));
    sessionStorage.setItem("sonora_src", "charts");
  }, [hub, country]);
  useEffect(() => () => applyHubGlow(null), []);
  return (
    <div data-testid="charts-page" className="reveal">
      <div className="eyebrow">Regional & Global</div>
      <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight mt-2">{t("charts")}</h1>
      <div className="flex flex-wrap gap-2 mt-6" data-testid="charts-hub-filter">
        {[{ key: "all", name: "All hubs", color: "#fff" }, ...ALL_HUBS].map((h) => (
          <button key={h.key} data-testid={`charts-hub-${h.key}`} onClick={() => setHub(h.key)}
            className={`px-4 h-9 rounded-full text-sm border transition-colors ${hub === h.key ? "bg-white text-black border-white" : "border-white/10 text-zinc-300 hover:border-white/30"}`}>{h.name}</button>
        ))}
      </div>
      <div className="grid lg:grid-cols-[1.4fr_1fr] gap-6 mt-8">
        <div className="surface p-5">
          <div className="flex items-center justify-between mb-2">
            <span className="flex items-center gap-2 font-display text-xl font-bold"><Globe2 size={18} className="text-[var(--hub)]" /> {t("topCountries")}</span>
            <Select value={country} onValueChange={setCountry}>
              <SelectTrigger data-testid="charts-country-select" className="w-48 bg-white/5 border-white/10"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-[#12121A] border-white/10">{Object.entries(COUNTRIES).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <WorldMap countries={d?.countries || []} selected={country} onSelect={setCountry} />
        </div>
        <div className="surface p-5">
          <div className="eyebrow mb-3">Streams · last 7 days</div>
          <table className="w-full text-sm" data-testid="charts-countries-table">
            <thead><tr className="text-zinc-500 text-xs font-mono uppercase"><th className="text-left py-2">#</th><th className="text-left">Country</th><th className="text-right">Streams</th><th className="text-right">Artists</th></tr></thead>
            <tbody>{(d?.countries || []).slice(0, 10).map((c, i) => (
              <tr key={c.code} onClick={() => setCountry(c.code)} className={`cursor-pointer border-t border-white/5 hover:bg-white/5 ${country === c.code ? "text-[var(--hub)]" : ""}`}>
                <td className="py-2.5 font-mono text-zinc-500">{i + 1}</td><td>{c.name}</td><td className="text-right font-mono">{fmtNum(c.streams)}</td><td className="text-right font-mono">{c.active_artists}</td>
              </tr>))}</tbody>
          </table>
        </div>
      </div>
      <div className="grid lg:grid-cols-[1.4fr_1fr] gap-6 mt-6">
        <div className="surface p-3" data-testid="charts-top-tracks">
          <div className="font-display text-xl font-bold px-3 pt-2 pb-3">{t("topTracks")} · {COUNTRIES[country]}</div>
          {d?.top_tracks.length === 0 && <div className="p-6 text-zinc-500 text-sm">No streams for this selection yet.</div>}
          {d?.top_tracks.map((tr, i) => <TrackRow key={tr.track_id} track={tr} list={d.top_tracks} rank={i + 1} />)}
        </div>
        <div className="space-y-6">
          <div className="surface p-3" data-testid="charts-top-artists">
            <div className="font-display text-xl font-bold px-3 pt-2 pb-2">{t("topArtists")}</div>
            {d?.top_artists.slice(0, 8).map((a, i) => <ArtistLine key={a.user_id} a={a} i={i} extra={fmtNum(a.chart_streams)} />)}
          </div>
          <div className="surface p-3" data-testid="charts-rising-stars">
            <div className="font-display text-xl font-bold px-3 pt-2 pb-2 flex items-center gap-2"><Flame size={18} className="text-orange-400" /> {t("rising")} · Tremplin</div>
            {d?.rising.length === 0 && <div className="p-4 text-zinc-500 text-sm">No rising artists here yet.</div>}
            {d?.rising.map((a, i) => <ArtistLine key={a.user_id} a={a} i={i} extra={<span className="text-emerald-400">+{a.growth_pct}%</span>} />)}
          </div>
        </div>
      </div>
      <p className="mt-6 text-xs font-mono text-zinc-600 flex items-center gap-2"><Clock size={12} /> Served from in-memory chart cache · refreshed {d?.refreshed_at ? new Date(d.refreshed_at).toLocaleTimeString() : "—"} · updates every 15 min</p>
    </div>
  );
}
